import {Component, OnDestroy, OnInit} from "@angular/core";
import { BINARY } from "src/app/config/constants";
import {Collection, DeviceStatus, EntityFilters, EntityStatusResponse, ISelectedSite} from "src/app/models/dashboard";
import { BackendService } from "src/app/services/backend/backend.service";
import { EventNotificationService, EventType } from "src/app/services/notification/event-notification.service";
import { SiteFilterService } from "src/app/services/site-filter.service";
import { RSocket, RSocketClient } from "rsocket-core";
import { Subscription } from "rxjs";
import { OAuthService } from 'angular-oauth2-oidc';
import {EntityType} from "../../models/api/entity-type";
import {AuthorizationService} from "../../services/authorization.service";


@Component({
    standalone: false,
    selector: "cc-collections",
    templateUrl: "./collections.component.html",
    styleUrls: ["./collections.component.scss"],
})
export class CollectionsComponent implements OnInit, OnDestroy {
    collections: Collection[] = [];
    mobileViewSelected = false;

	polledKPIPointsValuesSubscription: Subscription;

	collectionFiltersOptions: EntityFilters[] = [];
	selectedCollectionClassList: string[] = [];

	selectedSite: ISelectedSite;
	searchText: string = '';
	username: string = '';
	category = 'collection';
	firstSiteLoad = true;
	noOfPinnedCollections: number = null;
    entityStatus: { [key: string]: DeviceStatus } = {};
    entityOverriddenStatuses: { [key: string]: boolean };

	private rSocketClient: RSocketClient;
    private siteSubcription$ = new Subscription();
    constructor(
        private backendService: BackendService,
        private siteFilterService: SiteFilterService,
        private eventNotificationService: EventNotificationService,
        private authService: OAuthService,
        private authorizationService: AuthorizationService,
    ) {
    }

    ngOnInit() {
        this.username = this.authorizationService.getUsername();

		this.siteSubcription$ = this.siteFilterService.siteFilterChanged$.subscribe(selectedSite => {
			if (selectedSite === null) {
				return;
			}
			if (this.firstSiteLoad) {
				this.restoreFilters();
			}
			if (!this.firstSiteLoad) {
				this.resetFilters();
			}

			this.selectedSite = selectedSite;
			this.firstSiteLoad = false;
			this.clearPreviousData();
			this.getCollections();

		});

		this.eventNotificationService.getEventDispatcher$().subscribe((event) => {
			if (event.type === EventType.MOBILE_VIEW_SELECTED) {
				this.mobileViewSelected = <boolean>event.payload;
			}
		})
    }

    restoreFilters() {
		const filters = JSON.parse(sessionStorage.getItem(`${this.category}-filters`)) || null;

		if (filters) {
			this.searchText = filters.searchText;
			this.selectedCollectionClassList = filters.selectedClassList;
		}
	}

	resetFilters() {
		this.searchText = '';
		this.selectedCollectionClassList = [];
		sessionStorage.setItem(`${this.category}-filters`, JSON.stringify({ searchText: this.searchText, selectedClassList: this.selectedCollectionClassList }));
	}

	onFilterChangedHandler($event: any) {
		this.searchText = $event.searchText;
		this.selectedCollectionClassList = $event.selectedClassList;

		if ($event.action === 'apply') {
			this.filterCollections();
		}

		if ($event.action === 'clearFilter') {
			this.clearCollectionsFilters();
		}

		if ($event.action === 'clearSearch') {
			this.clearSearchInput();
		}
	}

	getCollections() {
		this.backendService.getDataByCategory(this.category, this.username, this.selectedSite, this.searchText, this.selectedCollectionClassList).subscribe((response: Collection[]) => {
			this.collections = response;
            this.loadEntityStatus(this.collections);
            this.loadEntityOverriddenStatus();
			this.noOfPinnedCollections = this.collections.filter(item => item.pinned).length;
			this.generateCollectionsFilters();
			this.callKpiPolling(response
				.flatMap((value) => value?.selectedKpi)
				.map((value: any) => value?.pointUUID))
		})
	}

	generateCollectionsFilters() {
		this.backendService.getCollectionsClassifications(this.selectedSite.siteId).subscribe((response: any[]) => {
			let collectionsClassification = {};

			response.map(item => {
				let collectionCategory = item.category.split("#")[1];
				if (!collectionsClassification[collectionCategory]) {
					collectionsClassification[collectionCategory] = [];
				}
				collectionsClassification[collectionCategory].push(item.entityClass.split("#")[1].replaceAll('_', ' '));
			});

			this.collectionFiltersOptions = Object.keys(collectionsClassification).map(category => {
				return {
					label: category.replaceAll('_', ' '),
					value: category,
					items: collectionsClassification[category].map((eqClass: string) => { return { 'label': eqClass.replaceAll('_', ' '), 'value': eqClass } })
				}
			});
		})
	}

	filterCollections() {
		this.backendService.getDataByCategory(this.category, this.username, this.selectedSite, this.searchText, this.selectedCollectionClassList)
			.subscribe((response: Collection[]) => {
				this.collections = response;
			});
	}

	clearFilters(searchText: string, equipmentClassList: string[]) {
		this.backendService.getDataByCategory(this.category, this.username, this.selectedSite, searchText, equipmentClassList)
			.subscribe((response: Collection[]) => {
				this.collections = response;
                this.loadEntityStatus(this.collections);
			});
	}

	clearSearchInput() {
		this.searchText = '';
		this.clearFilters(this.searchText, this.selectedCollectionClassList);
	}

	clearCollectionsFilters() {
		this.selectedCollectionClassList = [];
		this.clearFilters(this.searchText, this.selectedCollectionClassList);
	}

	clearPreviousData() {
        if (this.polledKPIPointsValuesSubscription) {
			this.polledKPIPointsValuesSubscription.unsubscribe();
		}
		this.closeRsocket();
        this.collections = [];
    }

    callKpiPolling(datapointIdListFilter: string[]) {
		////////
		// Create an instance of a rSocketClient
		this.rSocketClient = this.backendService.getRSocketClient();
		// Open the connection
		this.polledKPIPointsValuesSubscription = this.rSocketClient.connect().subscribe({
			onComplete: (socket: RSocket) => {
				let requestStream = 'request-stream-get-datapoint-list2';
				socket
					.requestStream({
						data: {
							'siteId': this.selectedSite.siteId,
							'datapointIdListFilter': datapointIdListFilter.filter(datapointId => datapointId !== null),
							'jwtToken': this.authService.getIdToken(),
							'interaction': 'Request'
						}, // null is a must if it does not include a message payload, else the Spring server side will not be matched.
						metadata: String.fromCharCode(requestStream.length) + requestStream
					})
					.subscribe({
						onComplete: () => console.log('complete'),
						onError: error => {
							console.log("Connection has been closed due to:: " + error);
						},
						onNext: payload => {
							this.mapCollectionKPI(payload.data);
						},
						onSubscribe: subscription => {
							subscription.request(1000000);
						},
					});

			},
			onError: error => {
				console.log("Connection has been refused due to:: " + error);
			},
			onSubscribe: cancel => {
				/* call cancel() to abort */
			}
		});

	}

	mapCollectionKPI(point: any) {
		for (let collection of this.collections) {
            const kpi = collection.selectedKpi;
            if (!!kpi && kpi.pointUUID === point.id) {
                kpi.presentValue = point.lastRecordedValue;
                kpi.signalType = point.signalType;
                kpi.type = point.type;
                kpi.units = point.units;

                if (point.signalType === BINARY) {
                    kpi.presentValue = point.lastRecordedValue ? true : false;
                }
            }
        }
	}

    ngOnDestroy() {
		if (this.polledKPIPointsValuesSubscription) {
			this.polledKPIPointsValuesSubscription.unsubscribe();
		}

		this.closeRsocket();
        this.siteSubcription$.unsubscribe();
	}

    onCardPinToggle(pinnedStatus: "PINNED" | "UNPINNED") {
        if (pinnedStatus === "PINNED") {
            this.noOfPinnedCollections++;
        } else {
            this.noOfPinnedCollections--;
        }
    }


    private closeRsocket() {
		if (this.rSocketClient) {
			this.rSocketClient.close();
		}
	}

    private loadEntityStatus(collections: Collection[]) {
        const params = {
            ...this.selectedSite,
            entityIds: collections.map((collection) => collection.id),
            entityType: EntityType.COLLECTION,
        }
        this.backendService.getEntitiesStatus(params).subscribe((response: EntityStatusResponse) => this.entityStatus = response.entityStatus);
    }

    private loadEntityOverriddenStatus() {
        this.backendService.loadEntityOverriddenStatus(this.collections, EntityType.COLLECTION, this.selectedSite).subscribe(response => {
            this.entityOverriddenStatuses = response.entityOverriddenStatuses;
        });
    }

    protected readonly EntityType = EntityType;
}
